import SaveIcon from '@mui/icons-material/Save'
import RestoreIcon from '@mui/icons-material/Restore'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import DownloadIcon from '@mui/icons-material/Download'
import SearchIcon from '@mui/icons-material/Search'
import PublishIcon from '@mui/icons-material/Publish'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  ColumnsPanelTrigger,
  gridFilteredSortedRowIdsSelector,
  GridRowSelectionModel,
  ToolbarButton,
  Toolbar,
  FilterPanelTrigger,
  QuickFilter,
  QuickFilterTrigger,
  QuickFilterControl,
  QuickFilterClear,
} from '@mui/x-data-grid-premium'
import { MutableRefObject, ReactNode, useState, useCallback, useMemo } from 'react'
import { downloadAsCsv } from 'Common/utils/utils'
import { AcceptedData } from 'export-to-csv/output/lib/types'
import { LoadingButton } from '@mui/lab'
// SimpleUpload removed - using permanent file input approach
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import {
  Badge,
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import FilterListIcon from '@mui/icons-material/FilterList'
import Divider from '@mui/material/Divider'
import CancelIcon from '@mui/icons-material/Cancel'

const excludedColumns = new Set(['id', 'actions', '__check__'])
const excelMayThrowIssueColumns = new Set(['batchGroupingCode', 'skuCode'])

export type MUIToolbarComponentProps = {
  hasUnsavedRows: boolean
  isSaving: boolean
  saveChanges: () => void
  discardChanges: () => void
  dataToDownload?: Record<string, AcceptedData>[]
  apiRef?: MutableRefObject<GridApiPremium> | null
  selectedRows: GridRowSelectionModel
  onBulkUpdateButtonClick?: () => void
  onDeleteRowsButtonClick?: () => void
  isDeleting?: boolean
  uploadData?: (file: File) => void
  isUploading?: boolean
  isLoading?: boolean
  onUndo?: () => void
  onRedo?: () => void
  canUndo?: boolean
  canRedo?: boolean
  customDownloadDataMethod?: (
    tableData: Record<string, AcceptedData>[]
  ) => Record<string, AcceptedData>[]
}

export const MUITableToolBarComponent = ({
  hasUnsavedRows,
  isSaving,
  saveChanges,
  discardChanges,
  dataToDownload,
  fileName,
  apiRef = null,
  selectedRows,
  onBulkUpdateButtonClick,
  onDeleteRowsButtonClick,
  isDeleting = false,
  uploadData,
  isUploading = false,
  customDownloadDataMethod = undefined,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  children,
}: MUIToolbarComponentProps & {
  fileName: string
  children?: ReactNode
}) => {
  const hasSelection =
    selectedRows &&
    selectedRows.ids &&
    ((selectedRows.type === 'include' && selectedRows.ids.size > 0) ||
      selectedRows.type === 'exclude')

  const filterPanelRender = useCallback(
    (props: any, state: any) => (
      <ToolbarButton {...props} color="default">
        <Badge
          badgeContent={state.filterCount}
          color="primary"
          variant="dot"
        >
          <FilterListIcon fontSize="small" />
        </Badge>
      </ToolbarButton>
    ),
    []
  )

  const columnsPanelRender = useMemo(() => <ToolbarButton />, [])

  const quickFilterTriggerRender = useCallback(
    (triggerProps: any, state: { expanded: boolean }) => (
      <Tooltip title="Search" enterDelay={0}>
        <ToolbarButton
          {...triggerProps}
          color="default"
          aria-disabled={state.expanded}
          sx={{
            opacity: state.expanded ? 0 : 1,
            pointerEvents: state.expanded ? 'none' : 'auto',
            position: state.expanded ? 'absolute' : 'relative',
          }}
        >
          <SearchIcon fontSize="small" />
        </ToolbarButton>
      </Tooltip>
    ),
    []
  )

  const quickFilterControlRender = useCallback(
    ({ ref, ...controlProps }: any, state: { expanded: boolean; value: string }) => (
      <TextField
        {...controlProps}
        inputRef={ref}
        aria-label="Search"
        placeholder="Search..."
        size="small"
        sx={{
          width: state.expanded ? 260 : 0,
          opacity: state.expanded ? 1 : 0,
          transition: 'width 0.2s ease, opacity 0.2s ease',
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: state.value ? (
              <InputAdornment position="end">
                <QuickFilterClear
                  edge="end"
                  size="small"
                  aria-label="Clear search"
                  material={{ sx: { marginRight: -0.75 } }}
                >
                  <CancelIcon fontSize="small" />
                </QuickFilterClear>
              </InputAdornment>
            ) : null,
            ...controlProps.slotProps?.input,
          },
          ...controlProps.slotProps,
        }}
      />
    ),
    []
  )

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: theme.palette.grey[100],
      })}
    >
      <Toolbar>
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          sx={{
            width: '100%',
          }}
        >
          <Stack display="flex" flexDirection="row">
            <LoadingButton
              disabled={!hasUnsavedRows || isSaving}
              loading={isSaving}
              onClick={saveChanges}
              startIcon={<SaveIcon />}
              loadingPosition="start"
            >
              <span>Save</span>
            </LoadingButton>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <LoadingButton
              disabled={!hasUnsavedRows || isSaving}
              onClick={discardChanges}
              startIcon={<RestoreIcon />}
              loadingPosition="start"
            >
              <span>Discard</span>
            </LoadingButton>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            {onUndo && (
              <Tooltip title="Undo (Ctrl+Z)">
                <span>
                  <LoadingButton disabled={!canUndo} onClick={onUndo} sx={{ minWidth: 36 }}>
                    <UndoIcon fontSize="small" />
                  </LoadingButton>
                </span>
              </Tooltip>
            )}
            {onRedo && (
              <Tooltip title="Redo (Ctrl+Y)">
                <span>
                  <LoadingButton disabled={!canRedo} onClick={onRedo} sx={{ minWidth: 36 }}>
                    <RedoIcon fontSize="small" />
                  </LoadingButton>
                </span>
              </Tooltip>
            )}
            {(onUndo || onRedo) && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}
            <LoadingButton
              disabled={!hasSelection || isSaving}
              onClick={onBulkUpdateButtonClick}
              startIcon={<SaveIcon />}
              loadingPosition="start"
            >
              <span>Bulk update</span>
            </LoadingButton>
            {onDeleteRowsButtonClick && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <LoadingButton
                  disabled={!hasSelection || isSaving || isDeleting}
                  loading={isDeleting}
                  onClick={onDeleteRowsButtonClick}
                  startIcon={<DeleteIcon />}
                  loadingPosition="start"
                  color="error"
                >
                  <span>Delete</span>
                </LoadingButton>
              </>
            )}
            {uploadData && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <LoadingButton
                  disabled={isUploading}
                  loading={isUploading}
                  onClick={() => {
                    const fileInput = document.getElementById(
                      'base-table-file-upload'
                    ) as HTMLInputElement
                    if (fileInput) {
                      fileInput.click()
                    } else {
                      console.error(
                        'MUITableToolBar: File input #base-table-file-upload not found!'
                      )
                    }
                  }}
                  startIcon={<PublishIcon />}
                  loadingPosition="start"
                >
                  <span>Upload</span>
                </LoadingButton>
              </>
            )}
            {children}
          </Stack>
          <Stack display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ mx: 0.5 }}
            />

            <Tooltip title="Columns">
              <ColumnsPanelTrigger render={columnsPanelRender}>
                <ViewColumnIcon fontSize="small" />
              </ColumnsPanelTrigger>
            </Tooltip>

            <Tooltip title="Filters">
              <FilterPanelTrigger render={filterPanelRender} />
            </Tooltip>

            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ mx: 0.5 }}
            />

            <ToolBarDownloadCSVButton
              fileName={fileName}
              data={dataToDownload ?? []}
              apiRef={apiRef}
              customDownloadDataMethod={customDownloadDataMethod}
            />
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ mx: 0.5 }}
            />
            <QuickFilter debounceMs={300}>
              <QuickFilterTrigger
                render={quickFilterTriggerRender}
              />
              <QuickFilterControl
                render={quickFilterControlRender}
              />
            </QuickFilter>
          </Stack>
        </Stack>
      </Toolbar>
    </Box>
  )
}

export const MUITableToolBar = ({
  hasUnsavedRows,
  isSaving,
  saveChanges,
  discardChanges,
  dataToDownload,
  fileName,
  apiRef = null,
  selectedRows,
  onBulkUpdateButtonClick,
  onDeleteRowsButtonClick,
  isDeleting = false,
  uploadData,
  isUploading = false,
  isLoading = false,
  customDownloadDataMethod = undefined,
}: MUIToolbarComponentProps & {
  fileName: string
}) => {
  if (isLoading) {
    return () => (
      <Toolbar>
        <CircularProgress size={18} />
      </Toolbar>
    )
  }

  return ({ children }: { children?: ReactNode }) => (
    <MUITableToolBarComponent
      hasUnsavedRows={hasUnsavedRows}
      isSaving={isSaving}
      saveChanges={saveChanges}
      discardChanges={discardChanges}
      dataToDownload={dataToDownload}
      fileName={fileName}
      apiRef={apiRef}
      selectedRows={selectedRows}
      onBulkUpdateButtonClick={onBulkUpdateButtonClick}
      onDeleteRowsButtonClick={onDeleteRowsButtonClick}
      isDeleting={isDeleting}
      uploadData={uploadData}
      isUploading={isUploading}
      customDownloadDataMethod={customDownloadDataMethod}
      children={children}
    />
  )
}

type ToolBarDownloadCSVButtonProps = {
  fileName: string
  data: Record<string, AcceptedData>[]
  apiRef?: MutableRefObject<GridApiPremium> | null
  customDownloadDataMethod?: (
    data: Record<string, AcceptedData>[]
  ) => Record<string, string>
}
const ToolBarDownloadCSVButton = ({
  fileName,
  data,
  apiRef = null,
  customDownloadDataMethod = undefined,
}: ToolBarDownloadCSVButtonProps) => {
  const [isDownloading, setIsDownloading] = useState(false)

  const prepareTableData = () => {
    if (!apiRef) {
      return []
    }
    if (customDownloadDataMethod) {
      return customDownloadDataMethod(data)
    }
    // Step 1: Get visible columns
    const visibleColumns = apiRef.current.getVisibleColumns()
    console.log('visibleColumns', visibleColumns)
    // Step 2: Get filtered and sorted rows
    const rowIds = gridFilteredSortedRowIdsSelector(apiRef) // Row IDs in sorted order
    const rowModels = apiRef.current.getRowModels() // Map of all rows

    // Step 3: Prepare the data
    const tableData = rowIds.map((id) => {
      const row = rowModels.get(id)
      const rowData: Record<string, AcceptedData> = {}
      if (row) {
        visibleColumns.forEach((col) => {
          if (excludedColumns.has(col.field)) {
            return
          }
          if (excelMayThrowIssueColumns.has(col.field)) {
            rowData[col.field] = `'${row[col.field]}`
            return
          }
          rowData[col.field] = row[col.field]
        })
      }
      return rowData
    })

    // Step 4: Prepare headers

    return tableData
  }

  const downloadFile = () => {
    setIsDownloading(true)
    if (apiRef) {
      try {
        const rows = prepareTableData()
        downloadAsCsv(fileName, rows)
      } catch (error) {
        console.error('Error preparing or downloading CSV:', error)
      }
    } else {
      try {
        downloadAsCsv(fileName, data)
      } catch (error) {
        console.error('Error downloading CSV:', error)
      }
    }
    setIsDownloading(false)
  }
  return (
    <LoadingButton
      loading={isDownloading}
      onClick={downloadFile}
      startIcon={<DownloadIcon />}
      loadingPosition="start"
    >
      <span>Download</span>
    </LoadingButton>
  )
}
